import { cn } from '@/lib/utils';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  // Optional tally rendered after the label. aria-hidden so the option's
  // accessible name stays the label alone and doesn't churn with the number.
  count?: number;
}

export function SegmentedToggle<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (next: T) => void;
  options: SegmentedOption<T>[];
}) {
  return (
    <div
      role="radiogroup"
      className="inline-flex w-fit items-center gap-1 rounded-full border border-(--color-border-strong) bg-neutral-50 p-1"
    >
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => {
              onChange(opt.value);
            }}
            className={cn(
              'text-body-sm h-8 min-w-32 rounded-full px-4 font-medium transition-colors',
              'focus-visible:shadow-(--shadow-focus-ring-solid) focus-visible:outline-none',
              active
                ? 'bg-surface text-accent-ink shadow-xs'
                : 'hover:text-accent-ink text-neutral-500',
            )}
          >
            {opt.label}
            {opt.count !== undefined && opt.count > 0 ? (
              <span
                aria-hidden
                className={cn(
                  'ml-1.5 inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] leading-none font-semibold',
                  active ? 'bg-primary-50 text-primary-700' : 'bg-neutral-200/70 text-neutral-600',
                )}
              >
                {opt.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
